import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DOMPurify from 'dompurify';

import Placeholder from '../Placeholder';
import './styles/preview_snippets.scss';

const SnippetPreview = ({
  previewRecipientId,
  previewContent,
  isLoadingPreviewContent,
  recipients,
  snippet,
  renderHtml,
  header,
  setPreviewRecipientId,
  message
}) => {
  const loadPreview = (recipient) => setPreviewRecipientId(recipient, snippet);

  useEffect(() => {
    if (recipients.length) {
      (async () => loadPreview(recipients[0]))();
    }
  }, []);

  if (isLoadingPreviewContent) {
    return <Placeholder.loading />;
  }

  if (!recipients.length) {
    return (
      <div className="preview-emails">
        There were no contacts that met your criteria, or the selected contacts lack adequate contact information.
      </div>
    );
  }

  if (!previewContent.content.body || previewContent.content.body.trim().length === 0) {
    return (
      <div className='preview-emails'>
        The generated body for your message is empty. Please, make sure the selected tokens have values.
      </div>
    );
  }

  return (
    <div className='preview-emails row'>
      {message && <div className='alert alert-warning'>{message}</div> }
      <div className='col-6'>
        <div className="card preview-snippets-recipient-list">
          <div className='card-body'>
            <ul className='nav nav-pills nav-stacked'>
              {recipients.map(recipient =>
                <li key={recipient.id} className='nav-item'>
                  <a
                    href='#'
                    className={classNames('nav-link', 'preview-snippets-recipient', { active: recipient.id === previewRecipientId })}
                    onClick={(e) => {
                      e.preventDefault();
                      loadPreview(recipient);
                    }}
                  >
                    <div className='preview-snippets-name'>
                      {recipient.name || `${recipient.first_name} ${recipient.last_name}`}
                    </div>
                    {recipient.relations && recipient.relations.map((relation) =>
                      <div className='preview-snippets-relation-text'>
                        {relation}
                      </div>
                    )}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      <div className='col-18'>
        <div className='preview-snippets-body'>
          <Placeholder.text ready={!!previewContent.content.body} width={560} lines={10}>
            <div>
              {header(previewContent)}

              {renderHtml ? (
                <div
                  className='preview-snippets-content fr-view'
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(previewContent.content.body, { ADD_ATTR: ['target', 'rel'] })
                  }}
                />
              ) : (
                <pre className='preview-snippets-content'>
                  {previewContent.content.body}
                </pre>
              )}
            </div>
          </Placeholder.text>
        </div>
      </div>
    </div>
  );
}

SnippetPreview.propTypes = {
  previewRecipientId: PropTypes.string,
  previewContent: PropTypes.shape({
    contact: PropTypes.object,
    recipient: PropTypes.object,
    content: PropTypes.object,
    schedule: PropTypes.object
  }),
  isLoadingPreviewContent: PropTypes.bool,
  recipients: PropTypes.array,
  snippet: PropTypes.object.isRequired,
  renderHtml: PropTypes.bool,
  header: PropTypes.func.isRequired,
  setPreviewRecipientId: PropTypes.func.isRequired,
  message: PropTypes.string
};

SnippetPreview.defaultProps = {
  previewContent: {
    contact: {},
    recipient: {},
    content: {},
    schedule: {}
  },
  isLoadingPreviewContent: false,
  recipients: [],
  previewRecipientId: null,
  snippet: {},
  renderHtml: true,
  header: () => false,
  setPreviewRecipientId: () => false,
  message: null
};

export default SnippetPreview;
